import { useState, useEffect, useCallback, useRef } from 'react';
import { SignedIn, useAuth } from '@clerk/clerk-react';
import { useSearchParams } from 'react-router-dom';
import { api } from '../utils/api';
import ItemForm from '../components/ItemForm';
import ImageWithPlaceholder from '../components/ImageWithPlaceholder';
import { getShareTextForItem } from '../utils/facebookShare';

export default function MyItems() {
  const { getToken } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [items, setItems] = useState([]);
  const [carouselIndices, setCarouselIndices] = useState({});
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [deletingItemId, setDeletingItemId] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState(() => searchParams.get('q') || '');
  const [focusItemId, setFocusItemId] = useState(() => {
    const raw = searchParams.get('itemId');
    const n = raw != null ? parseInt(raw, 10) : NaN;
    return Number.isFinite(n) ? n : null;
  });
  const [pendingUploads, setPendingUploads] = useState({});
  const [addCollapsed, setAddCollapsed] = useState(false);
  const [showBackToTop, setShowBackToTop] = useState(false);
  const [categories, setCategories] = useState([]);
  const [defaults, setDefaults] = useState([]);
  const [selectedDefaults, setSelectedDefaults] = useState([]);
  const [customCatName, setCustomCatName] = useState('');
  const [categoryBusy, setCategoryBusy] = useState(false);
  const [categoryMsg, setCategoryMsg] = useState(null);

  const topRef = useRef(null);
  const addSentinelRef = useRef(null);

  const fetchCategories = useCallback(async () => {
    try {
      const [mineRes, defRes] = await Promise.all([
        api('/api/categories/mine', { getToken }),
        api('/api/categories/defaults', {}),
      ]);
      if (mineRes.ok) setCategories(await mineRes.json());
      if (defRes.ok) {
        const d = await defRes.json();
        setDefaults(d);
        setSelectedDefaults(d);
      }
    } catch (_) { /* ignore */ }
  }, [getToken]);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api('/api/items/my-items', { getToken });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || `Error ${res.status}`);
      }
      const data = await res.json();
      setItems(data);
      const indices = {};
      data.forEach((item) => {
        indices[item.id] = 0;
      });
      setCarouselIndices(indices);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [getToken]);

  useEffect(() => {
    fetchItems();
    fetchCategories();
  }, [fetchItems, fetchCategories]);

  // Keep focus/search in sync when notification links change the URL while already on My Items
  useEffect(() => {
    const q = searchParams.get('q') || '';
    const raw = searchParams.get('itemId');
    const parsed = raw != null ? parseInt(raw, 10) : NaN;
    const nextId = Number.isFinite(parsed) ? parsed : null;
    setFocusItemId(nextId);
    if (q) {
      setSearchQuery(q);
    } else if (nextId != null) {
      const match = items.find((i) => i.id === nextId);
      if (match?.title) setSearchQuery(match.title);
    }
    if (nextId != null || q) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [searchParams, items]);

  const updateSearch = (value) => {
    setSearchQuery(value);
    setFocusItemId(null);
    const next = new URLSearchParams(searchParams);
    next.delete('itemId');
    if (value.trim()) next.set('q', value.trim());
    else next.delete('q');
    setSearchParams(next, { replace: true });
  };

  const seedSelectedDefaults = async () => {
    setCategoryBusy(true);
    setCategoryMsg(null);
    try {
      const res = await api('/api/categories/seed-defaults', {
        method: 'POST',
        getToken,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ names: selectedDefaults }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || 'Failed to add categories');
      }
      setCategories(await res.json());
      setCategoryMsg('Categories ready — you can create items now.');
    } catch (err) {
      setCategoryMsg(err.message);
    } finally {
      setCategoryBusy(false);
    }
  };

  const addCustomCategory = async () => {
    const name = customCatName.trim();
    if (!name) return;
    setCategoryBusy(true);
    setCategoryMsg(null);
    try {
      const res = await api('/api/categories', {
        method: 'POST',
        getToken,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || 'Failed to create category');
      }
      setCustomCatName('');
      await fetchCategories();
      setCategoryMsg(`Added “${name}”.`);
    } catch (err) {
      setCategoryMsg(err.message);
    } finally {
      setCategoryBusy(false);
    }
  };

  const toggleDefault = (name) => {
    setSelectedDefaults((prev) =>
      prev.includes(name) ? prev.filter((n) => n !== name) : [...prev, name]
    );
  };

  // Collapse full Add button into FAB once it scrolls off; show back-to-top after scroll
  useEffect(() => {
    const sentinel = addSentinelRef.current;
    if (!sentinel) return undefined;
    const io = new IntersectionObserver(
      ([entry]) => setAddCollapsed(!entry.isIntersecting),
      { root: null, threshold: 0, rootMargin: '-8px 0px 0px 0px' }
    );
    io.observe(sentinel);
    return () => io.disconnect();
  }, [loading]);

  useEffect(() => {
    const onScroll = () => setShowBackToTop(window.scrollY > 280);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const scrollToTop = () => {
    (topRef.current || document.documentElement).scrollIntoView({ behavior: 'smooth', block: 'start' });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleCarouselNext = (itemId, maxIndex) => {
    setCarouselIndices((prev) => ({
      ...prev,
      [itemId]: prev[itemId] < maxIndex ? prev[itemId] + 1 : 0,
    }));
  };

  const handleCarouselPrev = (itemId, maxIndex) => {
    setCarouselIndices((prev) => ({
      ...prev,
      [itemId]: prev[itemId] > 0 ? prev[itemId] - 1 : maxIndex,
    }));
  };

  const handleDownloadQr = async (item) => {
    if (!item?.id) {
      alert('QR code not available for this item');
      return;
    }
    try {
      const { downloadQrPng } = await import('../utils/downloadQr');
      const itemUrl = `${window.location.origin}/item/${item.id}`;
      await downloadQrPng(itemUrl, `item-${item.id}-qr.png`);
    } catch (err) {
      console.error('Error downloading QR code:', err);
      alert('Failed to download QR code');
    }
  };

  const handleFacebookShare = async (item) => {
    const text = getShareTextForItem(item);
    if (!text) {
      alert('Share text not available for this item');
      return;
    }
    try {
      await navigator.clipboard.writeText(text);
      window.open('https://www.facebook.com/marketplace/create/item', '_blank');
      alert('Listing copy ready — paste into Facebook. Buyers tap the link (no QR needed).');
    } catch (err) {
      console.error('Error copying to clipboard:', err);
      window.open('https://www.facebook.com/marketplace/create/item', '_blank');
      alert('Paste your item link manually into Facebook Marketplace.');
    }
  };

  const handleEdit = (item) => setEditingItem(item);
  const handleEditCancel = () => setEditingItem(null);

  const handleAddSuccess = (createdItem) => {
    setShowAddForm(false);
    if (createdItem?.imageUrls?.length) {
      setItems((prev) => [createdItem, ...prev]);
    } else {
      fetchItems();
    }
  };

  const uploadImagesInBackground = async (itemId, files, existingUrls = []) => {
    let uploadedUrls = [...existingUrls];
    try {
      if (existingUrls.length > 0) {
        await api(`/api/items/${itemId}/images`, {
          method: 'PUT',
          getToken,
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ imageUrls: existingUrls }),
        });
      }
      for (let i = 0; i < files.length; i++) {
        const formData = new FormData();
        formData.append('file', files[i]);
        const res = await api('/api/images/upload', {
          method: 'POST',
          getToken,
          body: formData,
        });
        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          throw new Error(data.error || 'Upload failed');
        }
        const data = await res.json();
        uploadedUrls.push(data.url);
        setItems((prev) =>
          prev.map((it) => (it.id === itemId ? { ...it, imageUrls: [...uploadedUrls] } : it))
        );
        await api(`/api/items/${itemId}/images`, {
          method: 'PUT',
          getToken,
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ imageUrls: uploadedUrls }),
        });
      }
    } catch (err) {
      console.error('Background upload failed:', err);
    } finally {
      setPendingUploads((prev) => {
        const next = { ...prev };
        delete next[itemId];
        return next;
      });
      fetchItems();
    }
  };

  const handleEditSuccess = () => {
    setEditingItem(null);
    fetchItems();
  };

  const handleDeleteClick = (itemId) => setDeletingItemId(itemId);
  const handleDeleteCancel = () => setDeletingItemId(null);

  const handleDeleteConfirm = async () => {
    if (!deletingItemId) return;
    setDeleteLoading(true);
    try {
      const res = await api(`/api/items/${deletingItemId}`, {
        getToken,
        method: 'DELETE',
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || `Error ${res.status}`);
      }
      setDeletingItemId(null);
      fetchItems();
    } catch (err) {
      alert(`Failed to delete item: ${err.message}`);
    } finally {
      setDeleteLoading(false);
    }
  };

  const searchLower = (searchQuery || '').trim().toLowerCase();
  const filteredItems = focusItemId != null
    ? items.filter((item) => item.id === focusItemId)
    : searchLower
      ? items.filter(
          (item) =>
            (item.title || '').toLowerCase().includes(searchLower) ||
            (item.description || '').toLowerCase().includes(searchLower)
        )
      : items;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <p className="text-2xl text-slate-500">Loading items…</p>
      </div>
    );
  }

  return (
    <SignedIn>
      <div ref={topRef} className="flex flex-col gap-6 pb-16">
        <h1 className="text-3xl font-semibold">My Items</h1>

        {categories.length === 0 && (
          <div className="rounded-2xl border-2 border-emerald-200 dark:border-emerald-800 bg-emerald-50/80 dark:bg-emerald-950/30 p-5 flex flex-col gap-4">
            <div>
              <h2 className="text-xl font-semibold text-slate-900 dark:text-slate-50">Set up categories first</h2>
              <p className="text-sm text-slate-600 dark:text-slate-300 mt-1">
                Categories organize your storefront (customers tap a shelf to browse). Pick starters below or add your own — ideas: Jewelry, Tools, Clothing, Antiques, Sports.
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              {defaults.map((name) => {
                const on = selectedDefaults.includes(name);
                return (
                  <button
                    key={name}
                    type="button"
                    onClick={() => toggleDefault(name)}
                    className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${
                      on
                        ? 'bg-emerald-600 text-white border-emerald-600'
                        : 'bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 border-slate-300 dark:border-slate-600'
                    }`}
                  >
                    {name}
                  </button>
                );
              })}
            </div>
            <div className="flex flex-col sm:flex-row gap-2">
              <button
                type="button"
                onClick={seedSelectedDefaults}
                disabled={categoryBusy || selectedDefaults.length === 0}
                className="h-12 px-5 rounded-xl bg-emerald-600 text-white font-semibold hover:bg-emerald-700 disabled:opacity-50"
              >
                {categoryBusy ? 'Saving…' : `Add selected (${selectedDefaults.length})`}
              </button>
              <div className="flex flex-1 gap-2">
                <input
                  type="text"
                  value={customCatName}
                  onChange={(e) => setCustomCatName(e.target.value)}
                  placeholder="Or type a custom category…"
                  className="h-12 flex-1 rounded-xl border border-slate-300 dark:border-slate-600 px-4 bg-white dark:bg-slate-800"
                />
                <button
                  type="button"
                  onClick={addCustomCategory}
                  disabled={categoryBusy || !customCatName.trim()}
                  className="h-12 px-4 rounded-xl border-2 border-emerald-600 text-emerald-700 dark:text-emerald-400 font-semibold disabled:opacity-50"
                >
                  Add
                </button>
              </div>
            </div>
            {categoryMsg && (
              <p className="text-sm text-slate-700 dark:text-slate-300">{categoryMsg}</p>
            )}
          </div>
        )}

        {categories.length > 0 && (
          <div className="flex flex-wrap items-center gap-2 text-sm text-slate-600 dark:text-slate-400">
            <span className="font-medium text-slate-800 dark:text-slate-200">Your categories:</span>
            {categories.map((c) => (
              <span key={c.id} className="px-2.5 py-1 rounded-full bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700">
                {c.name}
              </span>
            ))}
          </div>
        )}

        {error && (
          <p className="rounded-xl bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-200 p-4 text-lg">
            {error}
          </p>
        )}

        {items.length > 0 && (
          <div className="relative">
            <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" aria-hidden>
              🔍
            </span>
            <input
              type="search"
              value={searchQuery}
              onChange={(e) => updateSearch(e.target.value)}
              placeholder="Search your items…"
              className="w-full h-12 pl-12 pr-4 rounded-xl border-2 border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 placeholder-slate-500 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 outline-none transition-colors"
              aria-label="Search your items"
            />
            {searchQuery.trim() && (
              <button
                type="button"
                onClick={() => updateSearch('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-slate-200 dark:bg-slate-600 text-slate-600 dark:text-slate-300 flex items-center justify-center hover:bg-slate-300 dark:hover:bg-slate-500 transition-colors"
                aria-label="Clear search"
              >
                ×
              </button>
            )}
          </div>
        )}

        {/* Sentinel: when this leaves the viewport, collapse Add into FAB */}
        <div ref={addSentinelRef} className="h-0 w-full" aria-hidden />

        {!addCollapsed && (
          <button
            type="button"
            onClick={() => {
              if (categories.length === 0) {
                setCategoryMsg('Add at least one category above before creating items.');
                return;
              }
              setShowAddForm(true);
            }}
            disabled={categories.length === 0}
            className="h-16 text-2xl w-full rounded-xl font-semibold bg-indigo-600 text-white hover:bg-indigo-700 active:bg-indigo-800 transition-colors flex items-center justify-center gap-2 shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Add item
          </button>
        )}

        {items.length === 0 ? (
          <div className="rounded-xl border-2 border-slate-300 dark:border-slate-600 bg-slate-50 dark:bg-slate-800/50 p-8 text-center">
            <p className="text-xl text-slate-600 dark:text-slate-400">You haven&apos;t created any items yet.</p>
            <p className="text-lg text-slate-500 dark:text-slate-500 mt-2">Use the button above to list your first item.</p>
          </div>
        ) : filteredItems.length === 0 ? (
          <div className="rounded-xl border-2 border-slate-300 dark:border-slate-600 bg-slate-50 dark:bg-slate-800/50 p-8 text-center">
            <p className="text-xl text-slate-600 dark:text-slate-400">
              No items match &quot;{searchQuery.trim()}&quot;.
            </p>
            <p className="text-lg text-slate-500 dark:text-slate-500 mt-2">
              Try a different search or clear the search to see all items.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-6">
            {filteredItems.map((item) => {
              const currentImageIndex = carouselIndices[item.id] || 0;
              const imageUrls = item.imageUrls || [];
              const pending = pendingUploads[item.id];
              const totalSlots = pending ? Math.max(imageUrls.length, pending.count) : imageUrls.length;
              const hasSlots = totalSlots > 0;
              const currentSrc = currentImageIndex < imageUrls.length ? imageUrls[currentImageIndex] : null;
              const hasLowStock = item.stockCount < 3;
              const isOutOfStock = item.stockCount <= 0;

              return (
                <div
                  key={item.id}
                  className="rounded-xl border-2 border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 overflow-hidden"
                >
                  <div className="relative w-full aspect-square bg-slate-100 dark:bg-slate-900">
                    {hasSlots ? (
                      <>
                        <ImageWithPlaceholder
                          src={currentSrc}
                          alt={item.title}
                          className="w-full h-full object-cover"
                          containerClassName="absolute inset-0"
                        />
                        {totalSlots > 1 && (
                          <>
                            <button
                              type="button"
                              onClick={() => handleCarouselPrev(item.id, totalSlots - 1)}
                              className="absolute left-2 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/50 text-white flex items-center justify-center hover:bg-black/70 transition-colors"
                              aria-label="Previous image"
                            >
                              ‹
                            </button>
                            <button
                              type="button"
                              onClick={() => handleCarouselNext(item.id, totalSlots - 1)}
                              className="absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/50 text-white flex items-center justify-center hover:bg-black/70 transition-colors"
                              aria-label="Next image"
                            >
                              ›
                            </button>
                            <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1">
                              {Array.from({ length: totalSlots }, (_, idx) => (
                                <div
                                  key={idx}
                                  className={`w-2 h-2 rounded-full ${
                                    idx === currentImageIndex ? 'bg-white' : 'bg-white/50'
                                  }`}
                                />
                              ))}
                            </div>
                          </>
                        )}
                      </>
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-slate-400">No image</div>
                    )}
                  </div>

                  <div className="p-4 flex flex-col gap-3">
                    <h2 className="text-xl font-semibold line-clamp-2">{item.title}</h2>
                    <div className="flex items-center justify-between">
                      <span className="text-2xl font-bold text-indigo-600 dark:text-indigo-400">
                        ${parseFloat(item.price).toFixed(2)}
                      </span>
                      <div className="flex items-center gap-2">
                        {isOutOfStock ? (
                          <span className="px-3 py-1 rounded-full bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-200 text-sm font-medium">
                            Out of Stock
                          </span>
                        ) : hasLowStock ? (
                          <span className="px-3 py-1 rounded-full bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-200 text-sm font-medium">
                            Low Stock ({item.stockCount})
                          </span>
                        ) : (
                          <span className="px-3 py-1 rounded-full bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200 text-sm font-medium">
                            In Stock ({item.stockCount})
                          </span>
                        )}
                      </div>
                    </div>
                    {item.description && (
                      <p className="text-slate-600 dark:text-slate-400 line-clamp-2">{item.description}</p>
                    )}
                    <div className="flex flex-col gap-2 mt-2">
                      <div className="grid grid-cols-2 gap-2">
                        <button
                          type="button"
                          onClick={() => handleDownloadQr(item)}
                          disabled={!item?.id}
                          className="h-14 text-lg w-full rounded-xl font-semibold bg-slate-600 text-white hover:bg-slate-700 active:bg-slate-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
                        >
                          Download QR
                        </button>
                        <button
                          type="button"
                          onClick={() => handleFacebookShare(item)}
                          disabled={!item.title}
                          className="h-14 text-lg w-full rounded-xl font-semibold bg-blue-600 text-white hover:bg-blue-700 active:bg-blue-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
                        >
                          Share on Facebook
                        </button>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <button
                          type="button"
                          onClick={() => handleEdit(item)}
                          className="h-12 text-base w-full rounded-xl font-medium bg-indigo-600 text-white hover:bg-indigo-700 active:bg-indigo-800 transition-colors"
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDeleteClick(item.id)}
                          className="h-12 text-base w-full rounded-xl font-medium bg-red-600 text-white hover:bg-red-700 active:bg-red-800 transition-colors"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Floating Add (+) when scrolled past the top Add button */}
        {addCollapsed && (
          <button
            type="button"
            onClick={() => {
              if (categories.length === 0) {
                setCategoryMsg('Add at least one category above before creating items.');
                scrollToTop();
                return;
              }
              setShowAddForm(true);
            }}
            className="fixed z-40 right-4 bottom-24 sm:bottom-28 w-14 h-14 rounded-full bg-indigo-600 text-white shadow-xl hover:bg-indigo-700 active:bg-indigo-800 flex items-center justify-center touch-manipulation disabled:opacity-50"
            aria-label="Add item"
            title={categories.length === 0 ? 'Set up categories first' : 'Add item'}
            disabled={categories.length === 0}
          >
            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 4v16m8-8H4" />
            </svg>
          </button>
        )}

        {/* Back to top */}
        {showBackToTop && (
          <button
            type="button"
            onClick={scrollToTop}
            className={`fixed z-40 left-4 bottom-24 sm:bottom-28 w-12 h-12 rounded-full bg-[#232F3E] text-orange-400 border border-orange-500/40 shadow-xl hover:bg-[#37475A] flex items-center justify-center touch-manipulation ${
              addCollapsed ? '' : ''
            }`}
            aria-label="Back to top"
            title="Back to top"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 15l7-7 7 7" />
            </svg>
          </button>
        )}

        {showAddForm && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50"
            onClick={() => setShowAddForm(false)}
          >
            <div
              className="bg-white dark:bg-slate-800 rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6"
              onClick={(e) => e.stopPropagation()}
            >
              <h2 className="text-2xl font-semibold mb-4">Add new item</h2>
              <ItemForm onSuccess={handleAddSuccess} onCancel={() => setShowAddForm(false)} />
            </div>
          </div>
        )}

        {editingItem && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50"
            onClick={handleEditCancel}
          >
            <div
              className="bg-white dark:bg-slate-800 rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6"
              onClick={(e) => e.stopPropagation()}
            >
              <h2 className="text-2xl font-semibold mb-4">Edit item</h2>
              <ItemForm item={editingItem} onSuccess={handleEditSuccess} onCancel={handleEditCancel} />
            </div>
          </div>
        )}

        {deletingItemId && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50"
            onClick={handleDeleteCancel}
          >
            <div
              className="bg-white dark:bg-slate-800 rounded-xl shadow-xl max-w-md w-full p-6"
              onClick={(e) => e.stopPropagation()}
            >
              <h2 className="text-xl font-semibold mb-2">Delete this item?</h2>
              <p className="text-slate-600 dark:text-slate-400 mb-6">This cannot be undone.</p>
              <div className="flex gap-3 justify-end">
                <button
                  type="button"
                  onClick={handleDeleteCancel}
                  disabled={deleteLoading}
                  className="h-11 px-4 rounded-xl font-medium bg-slate-200 dark:bg-slate-700"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleDeleteConfirm}
                  disabled={deleteLoading}
                  className="h-11 px-4 rounded-xl font-medium bg-red-600 text-white disabled:opacity-50"
                >
                  {deleteLoading ? 'Deleting…' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </SignedIn>
  );
}
